import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, FileText, FileSignature, Check, X, 
  RotateCcw, Save, AlertCircle, Clock, Shield,
  ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { onboardingAPI } from '../../services/api';
import { toast } from 'sonner';

const OnboardingFormsSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedSection, setExpandedSection] = useState('documents');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await onboardingAPI.getSettings();
      setSettings(response.data);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load onboarding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentToggle = (docType, field, value) => {
    setSettings(prev => ({
      ...prev,
      documents: prev.documents.map(doc => 
        doc.type === docType ? { ...doc, [field]: value } : doc
      )
    }));
    setHasChanges(true);
  };

  const handleWaiverToggle = (waiverType, field, value) => {
    setSettings(prev => ({
      ...prev,
      waivers: prev.waivers.map(waiver => 
        waiver.type === waiverType ? { ...waiver, [field]: value } : waiver
      )
    }));
    setHasChanges(true);
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onboardingAPI.updateSettings(settings);
      toast.success('Onboarding settings saved successfully!');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all onboarding settings to defaults? This cannot be undone.')) return;
    
    setSaving(true);
    try {
      const response = await onboardingAPI.resetSettings();
      setSettings(response.data);
      toast.success('Settings reset to defaults');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to reset settings:', error);
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary)]"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-12 text-[var(--text-muted)]">
        <AlertCircle className="w-12 h-12 mx-auto mb-4" />
        <p>Failed to load settings. Please refresh and try again.</p>
      </div>
    );
  }

  const enabledDocsCount = settings.documents?.filter(d => d.enabled).length || 0;
  const enabledWaiversCount = settings.waivers?.filter(w => w.enabled).length || 0;

  return (
    <div className="space-y-6" data-testid="onboarding-forms-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--primary)]/20 flex items-center justify-center">
            <Settings className="w-5 h-5 text-[var(--primary)]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--text-primary)]">Onboarding Forms</h2>
            <p className="text-sm text-[var(--text-muted)]">Configure required documents and waivers for new workers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={saving}
            className="border-[var(--border-primary)] text-[var(--text-secondary)]"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[var(--primary)] text-black hover:bg-[var(--primary)]/90"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg"
        >
          <AlertCircle className="w-4 h-4 text-yellow-500" />
          <span className="text-sm text-yellow-400">You have unsaved changes</span>
        </motion.div>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--background-paper)] rounded-xl p-4 border border-[var(--border-primary)]">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-400" />
            <div>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{enabledDocsCount}</p>
              <p className="text-xs text-[var(--text-muted)]">Documents Required</p>
            </div>
          </div>
        </div>
        <div className="bg-[var(--background-paper)] rounded-xl p-4 border border-[var(--border-primary)]">
          <div className="flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-purple-400" />
            <div>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{enabledWaiversCount}</p>
              <p className="text-xs text-[var(--text-muted)]">Waivers Required</p>
            </div>
          </div>
        </div>
        <div className="bg-[var(--background-paper)] rounded-xl p-4 border border-[var(--border-primary)]">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-[var(--primary)]" />
            <div>
              <p className="text-2xl font-bold text-[var(--text-primary)]">{settings.deadline_days}</p>
              <p className="text-xs text-[var(--text-muted)]">Days to Complete</p>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border-primary)] overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'documents' ? '' : 'documents')}
          className="w-full flex items-center justify-between p-4 hover:bg-[var(--background-subtle)]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-400" />
            <span className="font-semibold text-[var(--text-primary)]">Required Documents</span>
            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full">
              {enabledDocsCount} enabled
            </span>
          </div>
          {expandedSection === 'documents' ? (
            <ChevronUp className="w-5 h-5 text-[var(--text-muted)]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
          )}
        </button>
        
        <AnimatePresence>
          {expandedSection === 'documents' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-[var(--border-primary)]"
            >
              <div className="p-4 space-y-3">
                {settings.documents?.map((doc) => (
                  <div 
                    key={doc.type}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      doc.enabled 
                        ? 'bg-[var(--background-subtle)] border-[var(--border-primary)]' 
                        : 'bg-[var(--background-subtle)]/30 border-[var(--border-primary)]/50 opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--text-primary)]">{doc.name}</span>
                        {doc.required && doc.enabled && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Required</span>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-xs text-[var(--text-muted)] mt-1">{doc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[var(--text-muted)]">Required</Label>
                        <Switch
                          checked={doc.required}
                          onCheckedChange={(checked) => handleDocumentToggle(doc.type, 'required', checked)}
                          disabled={!doc.enabled}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[var(--text-muted)]">Enabled</Label>
                        <Switch
                          checked={doc.enabled}
                          onCheckedChange={(checked) => handleDocumentToggle(doc.type, 'enabled', checked)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Waivers Section */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border-primary)] overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'waivers' ? '' : 'waivers')}
          className="w-full flex items-center justify-between p-4 hover:bg-[var(--background-subtle)]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-purple-400" />
            <span className="font-semibold text-[var(--text-primary)]">Required Waivers & Agreements</span>
            <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full">
              {enabledWaiversCount} enabled
            </span>
          </div>
          {expandedSection === 'waivers' ? (
            <ChevronUp className="w-5 h-5 text-[var(--text-muted)]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
          )}
        </button>
        
        <AnimatePresence>
          {expandedSection === 'waivers' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-[var(--border-primary)]"
            >
              <div className="p-4 space-y-3">
                {settings.waivers?.map((waiver) => (
                  <div 
                    key={waiver.type}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      waiver.enabled 
                        ? 'bg-[var(--background-subtle)] border-[var(--border-primary)]' 
                        : 'bg-[var(--background-subtle)]/30 border-[var(--border-primary)]/50 opacity-60'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-[var(--text-primary)]">{waiver.name}</span>
                        {waiver.required && waiver.enabled && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Required</span>
                        )}
                      </div>
                      {waiver.description && (
                        <p className="text-xs text-[var(--text-muted)] mt-1">{waiver.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[var(--text-muted)]">Required</Label>
                        <Switch
                          checked={waiver.required}
                          onCheckedChange={(checked) => handleWaiverToggle(waiver.type, 'required', checked)}
                          disabled={!waiver.enabled}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[var(--text-muted)]">Enabled</Label>
                        <Switch
                          checked={waiver.enabled}
                          onCheckedChange={(checked) => handleWaiverToggle(waiver.type, 'enabled', checked)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* General Settings Section */}
      <div className="bg-[var(--background-paper)] rounded-xl border border-[var(--border-primary)] overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'general' ? '' : 'general')}
          className="w-full flex items-center justify-between p-4 hover:bg-[var(--background-subtle)]/50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-[var(--primary)]" />
            <span className="font-semibold text-[var(--text-primary)]">General Settings</span>
          </div>
          {expandedSection === 'general' ? (
            <ChevronUp className="w-5 h-5 text-[var(--text-muted)]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
          )}
        </button>
        
        <AnimatePresence>
          {expandedSection === 'general' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-[var(--border-primary)]"
            >
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[var(--text-secondary)]">Completion Deadline (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={settings.deadline_days}
                      onChange={(e) => handleSettingChange('deadline_days', parseInt(e.target.value) || 7)}
                      className="input-styled mt-1"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">Workers must complete onboarding within this time</p>
                  </div>
                </div>
                
                <div className="space-y-3 pt-4 border-t border-[var(--border-primary)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-[var(--text-primary)]">Allow Partial Work</Label>
                      <p className="text-xs text-[var(--text-muted)]">Let workers start accepting jobs before completing all forms</p>
                    </div>
                    <Switch
                      checked={settings.allow_partial_work}
                      onCheckedChange={(checked) => handleSettingChange('allow_partial_work', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-[var(--text-primary)]">Require Background Check Approval</Label>
                      <p className="text-xs text-[var(--text-muted)]">Owner must manually approve background check before worker can work</p>
                    </div>
                    <Switch
                      checked={settings.require_background_check_approval}
                      onCheckedChange={(checked) => handleSettingChange('require_background_check_approval', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-[var(--text-primary)]">Auto-Approve on Completion</Label>
                      <p className="text-xs text-[var(--text-muted)]">Automatically approve workers when all forms are complete</p>
                    </div>
                    <Switch
                      checked={settings.auto_approve_on_completion}
                      onCheckedChange={(checked) => handleSettingChange('auto_approve_on_completion', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm text-[var(--text-primary)]">Send Deadline Reminders</Label>
                      <p className="text-xs text-[var(--text-muted)]">Email reminders to workers approaching their deadline</p>
                    </div>
                    <Switch
                      checked={settings.send_deadline_reminders}
                      onCheckedChange={(checked) => handleSettingChange('send_deadline_reminders', checked)}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Info Box */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-300">
          <p className="font-medium">How it works</p>
          <p className="text-blue-400/80 mt-1">
            Changes to these settings will apply to new workers starting onboarding. 
            Existing workers in progress will keep their current requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFormsSettings;
